
<script lang="ts" setup>
/**
 * Component used to show progress as a circular icon that fills from top, clockwise.
 *
 * Matches ProgressCircle.vue in go/agidb
 * CircularProgress is the name for the component on Figma
 */
import { computed } from 'vue'

const props = defineProps<{
  /**
   * Value between 0 and 100
   */
  percentage: number
}>()

const strokeWidth = 2
const radius = 5
const circumference = 2 * Math.PI * radius
// Results in a circle of 2 sections, one of which is painted, while the other isnt.
// This, combined with the dynamic offset defined next,
// results in a section of the circle sized based on percentage
const strokeDashArray = `${circumference} ${circumference}`

// This section is the equivalent of the percentage of the circle,
// but is positioned off by 90 degrees.
// In styles, we rotate the svg by 90 degrees to compensate
const strokeDashOffset = computed(() => circumference * ((100 - props.percentage) / 100))
</script>

<template>
  <div
    class="circular-progress"
    :class="percentage == 100 && 'circular-progress--complete'"
  >
    <svg :viewBox="`0 0 16 16`">
      <circle
        :r="radius"
        cx="8"
        cy="8"
        fill="transparent"
        stroke="var(--color-icon-subtle)"
        :stroke-width="strokeWidth"
        stroke-opacity="0.6"
        opacity="0.3"
      ></circle>
      <circle
        :r="radius"
        cx="8"
        cy="8"
        fill="transparent"
        stroke="currentColor"
        stroke-linecap="round"
        stroke-linejoin="round"
        :stroke-width="strokeWidth"
        :stroke-opacity="0.6"
        :stroke-dasharray="strokeDashArray"
        :stroke-dashoffset="strokeDashOffset"
      ></circle>
    </svg>
  </div>
</template>

<style lang="scss" scoped>
.circular-progress {
  display: inline-block;
  position: relative;
  width: 16px;
  height: 16px;

  color: var(--color-icon-primary);

  &--complete {
    color: var(--color-icon-warning);
  }

  svg {
    transform: rotate(-90deg);

    circle:last-child {
      transition: 'stroke-dashoffset 0.35s';
    }
  }
}
</style>
